import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import Reveal from "@/components/Reveal";

const PHOTOS = [
    {
        src: "/images/landscaping/fountain-shiva.jpg",
        alt: "Landscaped park feature with rockwork, water cascade and shaded canopy",
        caption: "Theme fountain & rockwork — park feature",
    },
    {
        src: "/images/landscaping/horse-sculpture.jpg",
        alt: "Sculpted horse-head feature set in a patterned hardscape bed with planter edging",
        caption: "Sculpted feature with patterned hardscape bed",
    },
    {
        src: "/images/landscaping/flowergate.jpg",
        alt: "Flowergate installation with floral edging in a landscaped park",
        caption: "Flowergate installation with floral edging",
    },
    {
        src: "/images/landscaping/umbrella-planter.jpg",
        alt: "Decorative umbrella-shaped planter with seasonal white flowers",
        caption: "Planter feature with seasonal flowers",
    },
    {
        src: "/images/landscaping/temple-court.jpg",
        alt: "Landscaped courtyard with paved flooring, seating and garden lighting",
        caption: "Landscaped courtyard with paving & lighting",
    },
];

export default function WorkGallery() {
    const [active, setActive] = useState(null);

    return (
        <section className="border-t border-slate-200 bg-background py-20 lg:py-28" data-testid="work-gallery">
            <div className="mx-auto max-w-7xl px-6 lg:px-12">
                <Reveal>
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">On the Ground</p>
                    <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                        Recent Landscaping Work
                    </h2>
                    <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
                        A selection of completed landscape and garden features — hardscape, themed installations and
                        planting executed with practical, maintainable design.
                    </p>
                </Reveal>
                <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {PHOTOS.map((photo, i) => (
                        <Reveal key={photo.src} delay={Math.min(i * 0.07, 0.35)}>
                            <button
                                onClick={() => setActive(photo)}
                                className="group block w-full text-left"
                                data-testid={`gallery-photo-${i}`}
                                aria-label={`View larger: ${photo.caption}`}
                            >
                                <div className="tick-frame overflow-hidden">
                                    <img
                                        src={photo.src}
                                        alt={photo.alt}
                                        loading="lazy"
                                        className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                </div>
                                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500 transition-colors duration-200 group-hover:text-navy">
                                    {photo.caption}
                                </p>
                            </button>
                        </Reveal>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {active && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[80] flex items-center justify-center bg-navy/90 p-6 backdrop-blur-sm"
                        onClick={() => setActive(null)}
                        data-testid="gallery-lightbox"
                        role="dialog"
                        aria-modal="true"
                        aria-label={active.caption}
                    >
                        <motion.figure
                            initial={{ scale: 0.94, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.94, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            className="relative max-h-full max-w-4xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={active.src}
                                alt={active.alt}
                                className="max-h-[80vh] w-auto max-w-full border border-slate-600 object-contain"
                            />
                            <figcaption className="mt-3 text-center font-mono text-xs uppercase tracking-[0.2em] text-slate-300">
                                {active.caption}
                            </figcaption>
                            <button
                                onClick={() => setActive(null)}
                                className="absolute -right-3 -top-3 flex h-10 w-10 items-center justify-center bg-amber text-white transition-colors duration-200 hover:bg-amber-hover"
                                aria-label="Close"
                                data-testid="gallery-lightbox-close"
                            >
                                <X size={18} />
                            </button>
                        </motion.figure>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
